import { useState, useCallback } from 'react';
import { notificationService, SendNotificationPayload, NotificationResponse } from '../services/NotificationService';

export interface UseNotificationsReturn {
  // State
  isSending: boolean;
  error: string | null;
  lastResponse: NotificationResponse | null;

  // Methods
  sendToUser: (userId: string, notification: Omit<SendNotificationPayload, 'user_id' | 'user_ids'>) => Promise<NotificationResponse | null>;
  sendToUsers: (userIds: string[], notification: Omit<SendNotificationPayload, 'user_id' | 'user_ids'>) => Promise<NotificationResponse | null>;
  
  // Convenience methods
  sendOrderUpdate: (userId: string, orderId: string, status: string, customMessage?: string) => Promise<NotificationResponse | null>;
  sendDeliveryUpdate: (userId: string, deliveryId: string, status: string, estimatedTime?: string) => Promise<NotificationResponse | null>;
  sendSellerConfirmation: (sellerId: string, requestId: string, buyerName: string, productName: string) => Promise<NotificationResponse | null>;
  sendChatMessage: (userId: string, chatId: string, senderName: string, message: string) => Promise<NotificationResponse | null>;
  sendPaymentUpdate: (userId: string, paymentId: string, status: string, amount?: number) => Promise<NotificationResponse | null>;
  sendNewOrder: (sellerId: string, orderId: string, vendorOrderId: string, buyerName: string, itemCount: number, totalAmount: number) => Promise<NotificationResponse | null>;
  
  // Utility methods
  clearError: () => void;
}

export function useNotifications(): UseNotificationsReturn {
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastResponse, setLastResponse] = useState<NotificationResponse | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const handleNotificationSend = useCallback(async <T extends any[]>(
    method: (...args: T) => Promise<NotificationResponse>,
    ...args: T
  ): Promise<NotificationResponse | null> => {
    try {
      setIsSending(true);
      setError(null);
      
      const response = await method(...args);
      setLastResponse(response);
      
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send notification';
      setError(errorMessage);
      console.error('Notification send error:', err);
      return null;
    } finally {
      setIsSending(false);
    }
  }, []);

  const sendToUser = useCallback((
    userId: string,
    notification: Omit<SendNotificationPayload, 'user_id' | 'user_ids'>
  ) => {
    return handleNotificationSend(
      notificationService.sendToUser.bind(notificationService),
      userId,
      notification
    );
  }, [handleNotificationSend]);

  const sendToUsers = useCallback((
    userIds: string[],
    notification: Omit<SendNotificationPayload, 'user_id' | 'user_ids'>
  ) => {
    return handleNotificationSend(
      notificationService.sendToUsers.bind(notificationService),
      userIds,
      notification
    );
  }, [handleNotificationSend]);

  const sendOrderUpdate = useCallback((
    userId: string,
    orderId: string,
    status: string,
    customMessage?: string
  ) => {
    return handleNotificationSend(
      notificationService.sendOrderStatusNotification.bind(notificationService),
      userId,
      orderId,
      status,
      customMessage
    );
  }, [handleNotificationSend]);

  const sendDeliveryUpdate = useCallback((
    userId: string,
    deliveryId: string,
    status: string,
    estimatedTime?: string
  ) => {
    return handleNotificationSend(
      notificationService.sendDeliveryUpdateNotification.bind(notificationService),
      userId,
      deliveryId,
      status,
      estimatedTime
    );
  }, [handleNotificationSend]);

  const sendSellerConfirmation = useCallback((
    sellerId: string,
    requestId: string,
    buyerName: string,
    productName: string
  ) => {
    return handleNotificationSend(
      notificationService.sendSellerConfirmationNotification.bind(notificationService),
      sellerId,
      requestId,
      buyerName,
      productName
    );
  }, [handleNotificationSend]);

  const sendChatMessage = useCallback((
    userId: string,
    chatId: string,
    senderName: string,
    message: string
  ) => {
    return handleNotificationSend(
      notificationService.sendChatMessageNotification.bind(notificationService),
      userId,
      chatId,
      senderName,
      message
    );
  }, [handleNotificationSend]);

  const sendPaymentUpdate = useCallback((
    userId: string,
    paymentId: string,
    status: string,
    amount?: number
  ) => {
    return handleNotificationSend(
      notificationService.sendPaymentUpdateNotification.bind(notificationService),
      userId,
      paymentId,
      status,
      amount
    );
  }, [handleNotificationSend]);

  const sendNewOrder = useCallback((
    sellerId: string,
    orderId: string,
    vendorOrderId: string,
    buyerName: string,
    itemCount: number,
    totalAmount: number
  ) => {
    return handleNotificationSend(
      notificationService.sendNewOrderNotification.bind(notificationService),
      sellerId,
      orderId,
      vendorOrderId,
      buyerName,
      itemCount,
      totalAmount
    );
  }, [handleNotificationSend]);

  return {
    // State
    isSending,
    error,
    lastResponse,

    // Methods
    sendToUser,
    sendToUsers,
    sendOrderUpdate,
    sendDeliveryUpdate,
    sendSellerConfirmation,
    sendChatMessage,
    sendPaymentUpdate,
    sendNewOrder,

    // Utility
    clearError,
  };
}